import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  Crown,
  TrendingUp,
  Users,
  DollarSign,
  Award,
  Settings,
  Save,
  Plus,
  Target,
  Percent,
  Edit,
  Trash2
} from "lucide-react";
import { mockMLMData, mockApi } from "../../mock";

const MLMConfiguration = ({ currentSettings, onSettingsChange }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const [mlmSettings, setMLMSettings] = useState({
    resellerUpgradeFee: 250.00,
    commissionStructure: mockMLMData.commissionStructure,
    achievementLevels: mockMLMData.achievementLevels,
    payoutSchedule: "monthly",
    minimumPayout: 50.00,
    referralBonuses: { directReferral: 25.00, teamBuilding: 100.00, levelUpgrade: 50.00 },
    maxMLMDepth: 5,
    enableReferralProgram: true,
    autoPayouts: true
  });

  // Load real MLM settings from backend on mount
  useEffect(() => {
    mockApi.getMLMData().then(r => {
      if (r?.success && r.data) {
        setMLMSettings(prev => ({ ...prev, ...r.data }));
      }
    });
  }, []);

  const [newAchievementLevel, setNewAchievementLevel] = useState({
    name: "",
    minMonthlyVolume: 0,
    maxMonthlyVolume: null,
    bonusPercentage: 0,
    benefits: []
  });

  const [editingLevelIndex, setEditingLevelIndex] = useState(null);
  const [editLevelDraft, setEditLevelDraft] = useState(null);

  const openEditLevel = (index) => {
    const lvl = mlmSettings.achievementLevels[index];
    setEditLevelDraft({
      name: lvl.name || '',
      minMonthlyVolume: lvl.minMonthlyVolume ?? 0,
      maxMonthlyVolume: lvl.maxMonthlyVolume ?? '',
      bonusPercentage: lvl.bonusPercentage ?? 0,
      benefitsText: (lvl.benefits || []).join('\n')
    });
    setEditingLevelIndex(index);
  };

  const saveEditLevel = async () => {
    if (editingLevelIndex === null) return;
    const updatedLevels = [...mlmSettings.achievementLevels];
    updatedLevels[editingLevelIndex] = {
      ...updatedLevels[editingLevelIndex],
      name: editLevelDraft.name,
      minMonthlyVolume: Number(editLevelDraft.minMonthlyVolume) || 0,
      maxMonthlyVolume: editLevelDraft.maxMonthlyVolume === '' ? null : Number(editLevelDraft.maxMonthlyVolume),
      bonusPercentage: Number(editLevelDraft.bonusPercentage) || 0,
      benefits: editLevelDraft.benefitsText.split('\n').map(b => b.trim()).filter(Boolean)
    };
    const newSettings = { ...mlmSettings, achievementLevels: updatedLevels };
    setMLMSettings(newSettings);
    const r = await mockApi.updateMLMSettings(newSettings);
    if (r.success) {
      toast({ title: 'Level updated', description: 'Achievement level saved to backend' });
    } else {
      toast({ title: 'Save failed', description: 'Could not save to backend', variant: 'destructive' });
    }
    setEditingLevelIndex(null);
    setEditLevelDraft(null);
  };

  const deleteLevel = async (index) => {
    if (!window.confirm(`Delete level "${mlmSettings.achievementLevels[index]?.name}"?`)) return;
    const updatedLevels = mlmSettings.achievementLevels.filter((_, i) => i !== index);
    const newSettings = { ...mlmSettings, achievementLevels: updatedLevels };
    setMLMSettings(newSettings);
    const r = await mockApi.updateMLMSettings(newSettings);
    if (r.success) {
      toast({ title: 'Level deleted', description: 'Achievement level removed' });
    } else {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const handleMLMSettingChange = (key, value) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      setMLMSettings(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setMLMSettings(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleCommissionChange = (level, value) => {
    const n = value === '' ? 0 : parseInt(value);
    setMLMSettings(prev => ({
      ...prev,
      commissionStructure: {
        ...prev.commissionStructure,
        [level]: Number.isFinite(n) ? n : 0
      }
    }));
  };

  const handleSaveMLMSettings = async () => {
    setLoading(true);
    try {
      const r = await mockApi.updateMLMSettings(mlmSettings);
      if (r.success) {
        toast({ title: "MLM Settings Saved", description: "Saved to backend successfully" });
        if (onSettingsChange) onSettingsChange("mlmSettings", mlmSettings);
      } else {
        toast({ title: "Save Failed", description: "Backend rejected the save", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Save Failed", description: error.message || "Network error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const addAchievementLevel = async () => {
    if (!newAchievementLevel.name || newAchievementLevel.minMonthlyVolume < 0) {
      toast({ title: "Invalid Level", description: "Please enter valid level details", variant: "destructive" });
      return;
    }
    const updatedLevels = [...(mlmSettings.achievementLevels || []), { ...newAchievementLevel, id: Date.now() }];
    const newSettings = { ...mlmSettings, achievementLevels: updatedLevels };
    setMLMSettings(newSettings);
    const r = await mockApi.updateMLMSettings(newSettings);
    if (r.success) {
      setNewAchievementLevel({ name: "", minMonthlyVolume: 0, maxMonthlyVolume: null, bonusPercentage: 0, benefits: [] });
      toast({ title: "Level Added", description: "Saved to backend" });
    } else {
      toast({ title: "Save failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Crown className="h-6 w-6 text-yellow-600" />
            <span>MLM System Configuration</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pricing" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="pricing">Pricing & Fees</TabsTrigger>
              <TabsTrigger value="commissions">Commissions</TabsTrigger>
              <TabsTrigger value="levels">Achievement Levels</TabsTrigger>
              <TabsTrigger value="referrals">Referral Program</TabsTrigger>
            </TabsList>

            {/* Pricing & Fees Tab */}
            <TabsContent value="pricing" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Reseller Program Pricing</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="upgradeFee">Annual Upgrade Fee ($)</Label>
                      <Input
                        id="upgradeFee"
                        type="number"
                        value={Number.isFinite(mlmSettings.resellerUpgradeFee) ? mlmSettings.resellerUpgradeFee : ''}
                        onChange={(e) => handleMLMSettingChange("resellerUpgradeFee", parseFloat(e.target.value))}
                        className="text-lg font-semibold"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Amount customers pay to upgrade to reseller status
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="minPayout">Minimum Payout ($)</Label>
                      <Input
                        id="minPayout"
                        type="number"
                        value={Number.isFinite(mlmSettings.minimumPayout) ? mlmSettings.minimumPayout : ''}
                        onChange={(e) => handleMLMSettingChange("minimumPayout", parseFloat(e.target.value))}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Minimum commission amount for payout
                      </p>
                    </div>

                    <div>
                      <Label>Payout Schedule</Label>
                      <Select 
                        value={mlmSettings.payoutSchedule} 
                        onValueChange={(value) => handleMLMSettingChange("payoutSchedule", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Automatic Payouts</Label>
                      <Switch
                        checked={mlmSettings.autoPayouts}
                        onCheckedChange={(checked) => handleMLMSettingChange("autoPayouts", checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">System Limits</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="maxDepth">Maximum MLM Depth</Label>
                      <Select 
                        value={(mlmSettings.maxMLMDepth ?? 5).toString()} 
                        onValueChange={(value) => handleMLMSettingChange("maxMLMDepth", parseInt(value))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 Levels</SelectItem>
                          <SelectItem value="5">5 Levels</SelectItem>
                          <SelectItem value="7">7 Levels</SelectItem>
                          <SelectItem value="10">10 Levels</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Current Configuration</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <p><strong>Upgrade Fee:</strong> ${mlmSettings.resellerUpgradeFee}</p>
                        <p><strong>Min Payout:</strong> ${mlmSettings.minimumPayout}</p>
                        <p><strong>Schedule:</strong> {mlmSettings.payoutSchedule}</p>
                        <p><strong>Max Depth:</strong> {mlmSettings.maxMLMDepth} levels</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Commission Structure Tab */}
            <TabsContent value="commissions" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Multi-Level Commission Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(mlmSettings.commissionStructure).map(([level, rate]) => (
                      <div key={level} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-semibold capitalize">{level.replace('level', 'Level ')}</h4>
                          <p className="text-sm text-gray-600">
                            {level === 'level1' ? 'Direct sales commission' :
                             level === 'level2' ? 'Second tier team commission' :
                             level === 'level3' ? 'Third tier team commission' :
                             level === 'level4' ? 'Fourth tier team commission' :
                             'Fifth tier team commission'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Input
                            type="number"
                            value={Number.isFinite(rate) ? rate : ''}
                            onChange={(e) => handleCommissionChange(level, e.target.value)}
                            className="w-20 text-center"
                            min="0"
                            max="50"
                          />
                          <span className="text-lg font-semibold">%</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-2">Commission Example</h4>
                    <p className="text-sm text-green-800">
                      On a $100 sale: Level 1 earns ${mlmSettings.commissionStructure.level1}, 
                      Level 2 earns ${mlmSettings.commissionStructure.level2}, 
                      Level 3 earns ${mlmSettings.commissionStructure.level3}, etc.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Achievement Levels Tab */}
            <TabsContent value="levels" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Current Achievement Levels</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(mlmSettings.achievementLevels || []).length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">No achievement levels yet. Add one below.</p>
                      )}
                      {(mlmSettings.achievementLevels || []).map((level, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Badge className={
                                level.name === 'Bronze' ? 'bg-orange-100 text-orange-800' :
                                level.name === 'Silver' ? 'bg-gray-100 text-gray-800' :
                                level.name === 'Gold' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-purple-100 text-purple-800'
                              }>
                                {level.name || 'Unnamed'}
                              </Badge>
                              <span className="text-sm font-medium">+{level.bonusPercentage ?? 0}% Bonus</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button size="sm" variant="outline" onClick={() => openEditLevel(index)} title="Edit level">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => deleteLevel(index)} title="Delete level">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600">
                            ${level.minMonthlyVolume ?? 0} - {level.maxMonthlyVolume ? `$${level.maxMonthlyVolume}` : 'Unlimited'} monthly
                          </p>
                          <div className="mt-2">
                            {(level.benefits || []).map((benefit, bIndex) => (
                              <p key={bIndex} className="text-xs text-gray-500">• {benefit}</p>
                            ))}
                          </div>
                        </div>
                      ))}

                      {/* Edit Achievement Level Dialog */}
                      <Dialog open={editingLevelIndex !== null} onOpenChange={(open) => { if (!open) { setEditingLevelIndex(null); setEditLevelDraft(null); } }}>
                        <DialogContent className="max-w-lg">
                          <DialogHeader>
                            <DialogTitle>Edit Achievement Level</DialogTitle>
                          </DialogHeader>
                          {editLevelDraft && (
                            <div className="space-y-4 py-2">
                              <div>
                                <Label>Level Name</Label>
                                <Input value={editLevelDraft.name} onChange={(e) => setEditLevelDraft({ ...editLevelDraft, name: e.target.value })} placeholder="Bronze / Silver / Gold / Platinum / Custom" />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Min Monthly Volume ($)</Label>
                                  <Input type="number" value={editLevelDraft.minMonthlyVolume ?? ''} onChange={(e) => setEditLevelDraft({ ...editLevelDraft, minMonthlyVolume: e.target.value })} />
                                </div>
                                <div>
                                  <Label>Max Monthly Volume ($, blank = unlimited)</Label>
                                  <Input type="number" value={editLevelDraft.maxMonthlyVolume ?? ''} onChange={(e) => setEditLevelDraft({ ...editLevelDraft, maxMonthlyVolume: e.target.value })} />
                                </div>
                              </div>
                              <div>
                                <Label>Bonus Percentage (%)</Label>
                                <Input type="number" value={editLevelDraft.bonusPercentage ?? ''} onChange={(e) => setEditLevelDraft({ ...editLevelDraft, bonusPercentage: e.target.value })} />
                              </div>
                              <div>
                                <Label>Benefits (one per line)</Label>
                                <textarea
                                  className="w-full min-h-[100px] border rounded p-2 text-sm"
                                  value={editLevelDraft.benefitsText}
                                  onChange={(e) => setEditLevelDraft({ ...editLevelDraft, benefitsText: e.target.value })}
                                  placeholder="15% commission boost&#10;Marketing materials&#10;Priority support"
                                />
                              </div>
                            </div>
                          )}
                          <DialogFooter>
                            <Button variant="outline" onClick={() => { setEditingLevelIndex(null); setEditLevelDraft(null); }}>Cancel</Button>
                            <Button onClick={saveEditLevel}>Save changes</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Add Achievement Level</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Level Name</Label>
                      <Input
                        value={newAchievementLevel.name}
                        onChange={(e) => setNewAchievementLevel(prev => ({...prev, name: e.target.value}))}
                        placeholder="e.g., Diamond"
                      />
                    </div>
                    
                    <div>
                      <Label>Minimum Monthly Volume ($)</Label>
                      <Input
                        type="number"
                        value={Number.isFinite(newAchievementLevel.minMonthlyVolume) ? newAchievementLevel.minMonthlyVolume : ''}
                        onChange={(e) => setNewAchievementLevel(prev => ({...prev, minMonthlyVolume: parseInt(e.target.value)}))}
                      />
                    </div>
                    
                    <div>
                      <Label>Commission Bonus (%)</Label>
                      <Input
                        type="number"
                        value={Number.isFinite(newAchievementLevel.bonusPercentage) ? newAchievementLevel.bonusPercentage : ''}
                        onChange={(e) => setNewAchievementLevel(prev => ({...prev, bonusPercentage: parseInt(e.target.value)}))}
                        max="50"
                      />
                    </div>

                    <Button onClick={addAchievementLevel} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Achievement Level
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Referral Program Tab */}
            <TabsContent value="referrals" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Referral Bonus System</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                      <Label className="text-base font-semibold">Enable Referral Program</Label>
                      <Switch
                        checked={mlmSettings.enableReferralProgram}
                        onCheckedChange={(checked) => handleMLMSettingChange("enableReferralProgram", checked)}
                      />
                    </div>

                    {mlmSettings.enableReferralProgram && (
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <Target className="h-8 w-8 text-blue-600 mb-2" />
                          <Label>Direct Referral Bonus ($)</Label>
                          <Input
                            type="number"
                            value={Number.isFinite(mlmSettings.referralBonuses?.directReferral) ? mlmSettings.referralBonuses.directReferral : ''}
                            onChange={(e) => handleMLMSettingChange("referralBonuses.directReferral", parseFloat(e.target.value))}
                            className="mt-2"
                          />
                          <p className="text-xs text-blue-600 mt-1">Per new reseller recruited</p>
                        </div>

                        <div className="p-4 bg-purple-50 rounded-lg">
                          <Users className="h-8 w-8 text-purple-600 mb-2" />
                          <Label>Team Building Bonus ($)</Label>
                          <Input
                            type="number"
                            value={Number.isFinite(mlmSettings.referralBonuses?.teamBuilding) ? mlmSettings.referralBonuses.teamBuilding : ''}
                            onChange={(e) => handleMLMSettingChange("referralBonuses.teamBuilding", parseFloat(e.target.value))}
                            className="mt-2"
                          />
                          <p className="text-xs text-purple-600 mt-1">When team reaches 10 members</p>
                        </div>

                        <div className="p-4 bg-green-50 rounded-lg">
                          <Award className="h-8 w-8 text-green-600 mb-2" />
                          <Label>Level Upgrade Bonus ($)</Label>
                          <Input
                            type="number"
                            value={Number.isFinite(mlmSettings.referralBonuses?.levelUpgrade) ? mlmSettings.referralBonuses.levelUpgrade : ''}
                            onChange={(e) => handleMLMSettingChange("referralBonuses.levelUpgrade", parseFloat(e.target.value))}
                            className="mt-2"
                          />
                          <p className="text-xs text-green-600 mt-1">When referral achieves new level</p>
                        </div>
                      </div>
                    )}

                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-900 mb-2">Referral Program Benefits</h4>
                      <div className="text-sm text-yellow-800 space-y-1">
                        <p>• Encourages active recruitment and team building</p>
                        <p>• Provides additional income streams for resellers</p>
                        <p>• Creates viral growth through incentivized sharing</p>
                        <p>• Rewards both direct and indirect team development</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end pt-6 border-t">
            <Button 
              onClick={handleSaveMLMSettings}
              disabled={loading}
              className="bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 px-8"
            >
              {loading ? (
                "Saving..."
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save MLM Configuration
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MLMConfiguration;